import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { usersService } from "@/services/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  Settings, 
  FileText, 
  Edit, 
  Trash2, 
  Save, 
  X,
  Loader2,
  Search
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const roles = ["COO", "CEO", "Overall Marketing Team Lead", "Team Lead", "Assistant Team Lead", "Team Lead/ HR Manager", "HR Manager", "Team Member", "HR Intern"];
const departments = [
  "Social Media Ambassadors",
  "HR",
  "Email Marketing",
  "Client Relations",
  "Creativity",
  "Content Creation",
  "Public Relations",
  "Digital Marketing",
  "Executive"
];

const Admin = () => {
  const { userData, refreshUserData } = useAuth();
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (activeTab === "users") fetchUsers();
  }, [activeTab]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await usersService.getAll();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      role: user.role || "Team Member",
      department: user.department || "General",
      permissions: user.permissions || ["view"]
    });
  };

  const handleSaveUser = async () => {
    try {
      await usersService.update(editingUser.id, editForm);
      toast.success("User updated successfully");
      setEditingUser(null);
      fetchUsers();
      await refreshUserData();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await usersService.delete(id);
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const colors = {
      "COO": "bg-red-100 text-red-700",
      "CEO": "bg-purple-100 text-purple-700",
      "Marketing Team Lead": "bg-purple-100 text-purple-700",
      "Team Lead": "bg-blue-100 text-blue-700",
      "Assistant Team Lead": "bg-amber-100 text-amber-700",
      "HR Manager": "bg-green-100 text-green-700",
      "Team Member": "bg-gray-100 text-gray-700",
      "HR Intern": "bg-cyan-100 text-cyan-700"
    };
    return colors[role] || "bg-gray-100 text-gray-700";
  };

  const tabs = [
    { id: "users", name: "User Management", icon: Users },
    { id: "settings", name: "System Settings", icon: Settings },
    { id: "logs", name: "Audit Logs", icon: FileText },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-navy">Admin Panel</h1>
        <p className="text-gray-500">Manage system settings and configurations</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {tabs.map((tab) => (
          <Card 
            key={tab.id}
            className={`cursor-pointer hover:shadow-md transition-all ${activeTab === tab.id ? 'ring-2 ring-action-blue' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <CardContent className="pt-5 flex items-center gap-4">
              <div className="w-12 h-12 bg-action-blue/10 rounded-lg flex items-center justify-center">
                <tab.icon className="w-6 h-6 text-action-blue" />
              </div>
              <div>
                <h3 className="font-semibold text-navy">{tab.name}</h3>
                <p className="text-sm text-gray-500">
                  {tab.id === "users" ? `${users.length} users` : 
                   tab.id === "settings" ? "Configure options" : 
                   "View activity logs"}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {activeTab === "users" && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>All Users</CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-action-blue" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Name</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Email</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Role</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Department</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">{user.name}</td>
                        <td className="py-3 px-4 text-gray-500">{user.email}</td>
                        <td className="py-3 px-4">
                          <Badge className={getRoleBadge(user.role)}>{user.role}</Badge>
                        </td>
                        <td className="py-3 px-4">{user.department}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleEditUser(user)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(user.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === "settings" && (
        <Card>
          <CardHeader>
            <CardTitle>System Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Organization Name</label>
              <Input defaultValue="Havana Group Operations" className="max-w-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default User Role</label>
              <select className="px-4 py-2 border border-slate-200 rounded-lg max-w-md">
                {roles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Allow Self Registration</label>
              <div className="flex items-center gap-2">
                <input type="checkbox" defaultChecked className="w-4 h-4" />
                <span className="text-sm text-gray-500">Enable user self-registration</span>
              </div>
            </div>
            <Button className="mt-4">
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </Button>
          </CardContent>
        </Card>
      )}

      {activeTab === "logs" && (
        <Card>
          <CardHeader>
            <CardTitle>Audit Logs</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-500 text-center py-8">No activity logs to display yet.</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <Input 
                value={editForm.name} 
                onChange={(e) => setEditForm({...editForm, name: e.target.value})} 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <select 
                value={editForm.role} 
                onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg"
              >
                {roles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
              <select 
                value={editForm.department} 
                onChange={(e) => setEditForm({...editForm, department: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg"
              >
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSaveUser} className="bg-white text-black border border-gray-300 hover:bg-gray-100">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
